import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useStore } from "../../store/store";
import { generateSignedEndpoint } from "../../api/generateSignedEndpoint";
import { IUser } from "../../InteractionPage/store/store";
import { useLocation } from "react-router-dom";
import { ISubscriber } from "../../LivePage/hooks/messages/types";

export type LiveType = {
	title: string;
	color1: string;
	color2: string;
	type: string;
};
export type LiveTextOptions = {
	date: boolean;
	title: string;
	duration: boolean;
  };
  
  export type LiveSkin = {
	id: number;
	name: string;
	creatorId: number;
	primaryColor: string;
	secondaryColor: string;
	primaryTextColor: string;
	secondaryTextColor: string;
	footerColor: string | null;
	sidebarColor: string | null;
	logo: string;
	beforeBackground: string;
	afterBackground: string;
	bluredBeforeBackground: boolean;
	bluredAfterBackground: boolean;
	beforeLiveTextOptions: LiveTextOptions;
	afterLiveTextOptions: LiveTextOptions;
	createdAt: string; // ISO 8601 date string
  };

interface IConf {
	id: number;
	video: {
		id: number;
		title: string;
		file_title: string;
		url: string;
	};
}

interface IFile {
	format: string;
	id: number;
	name: string;
	title: string;
	url: string;
}
export interface ILiveDetails {
	id: number;
	title: string;
	description: string;
	startDate: string;
	date: {
		StartDate: string;
		EndDate: string;
	};
	url: string;
	created_by: number;
	admin_info: {
		email: string;
		first_name: string;
		last_name: string;
		subscription_id: string;
	};
	status: 1 | 0 | -1;
	active: 1 | 0;
	thumbnail: string;
	guests: ISubscriber[];
	canComment: boolean;
	chat: boolean;
	isCommentValidation: boolean;
	videoEmbedUrl: string;
	canReplyToComments: boolean;
	tags: string;
	subscription: boolean;
	isSecured: boolean;
	conf: IConf;
	isIntranet: boolean;
	canCommentBeforeLive: boolean;
	private_chat: boolean;
	canShareFile: boolean;
	files: IFile[];
	docPrivate: boolean;
	docPublic: boolean;
	docPublicMail: boolean;
	poll: boolean;
	types:LiveType[];
	liveSkin:LiveSkin
}
export interface ILiveDetailsResponse {
	live: ILiveDetails;
	/** When request includes a subscription/token, backend may return the participant's fullName so they can join without the modal */
	subscriberFullName?: string;
}

function normalizeLive(live: ILiveDetails & Record<string, unknown>): ILiveDetails {
	if (!live) return live;
	// Some APIs return snake_case; support both so flags like docPublic are never undefined.
	const docPublic =
		(live as { docPublic?: boolean }).docPublic ??
		(live as { doc_public?: boolean }).doc_public ??
		false;
	const docPrivate =
		(live as { docPrivate?: boolean }).docPrivate ??
		(live as { doc_private?: boolean }).doc_private ??
		false;
	const docPublicMail =
		(live as { docPublicMail?: boolean }).docPublicMail ??
		(live as { doc_public_mail?: boolean }).doc_public_mail ??
		false;
	const canShareFile =
		(live as { canShareFile?: boolean }).canShareFile ??
		(live as { can_share_file?: boolean }).can_share_file ??
		false;
	return { ...live, docPublic, docPrivate, docPublicMail, canShareFile };
}

const fetchLiveDetails = async (
	liveToken: string | undefined,
	livePassword: string | undefined | null,
	user: IUser | null,
	queryToken: string | null
): Promise<ILiveDetailsResponse> => {
	let endpoint = "/live/by-token/" + liveToken;
	const hasQuery = () => endpoint.includes("?");
	if (livePassword) {
		endpoint += "?token=" + livePassword;
	}
	if (livePassword && user?.fullName) {
		endpoint += "&fullName=" + encodeURIComponent(user.fullName);
	} else if (!livePassword && user?.fullName) {
		endpoint += (hasQuery() ? "&" : "?") + "fullName=" + encodeURIComponent(user.fullName);
	}
	if (queryToken) {
		endpoint += (hasQuery() ? "&" : "?") + "token=" + encodeURIComponent(queryToken);
	}
	const signedUrl = await generateSignedEndpoint(endpoint);
	const response = await axios.get(signedUrl);
	const raw: ILiveDetailsResponse = response.data.data;
	if (raw?.live) {
		return { ...raw, live: normalizeLive(raw.live as ILiveDetails & Record<string, unknown>) };
	}
	return raw;
};
const useGetLiveDetails = (liveToken: string | undefined) => {
	const cryptedLivePassword = useStore((state) => state.cryptedLivePassword);
	const livePasswordFromToken = useStore((state) => state.user);
	const livePassword = cryptedLivePassword || livePasswordFromToken?.livePassword;
	const setIsCommentValidation = useStore((state) => state.setIsCommentValidation);
	const user = useStore((state) => state.user);
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const queryToken = queryParams.get("token");
	return useQuery({
		queryKey: ["liveDetails", liveToken, livePassword, user, queryToken],
		queryFn: () => fetchLiveDetails(liveToken, livePassword, user, queryToken),
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
		enabled: !!liveToken,
		onSuccess(data) {
			setIsCommentValidation(data.live.isCommentValidation);
		},
	});
};

export default useGetLiveDetails;
